import { useDarkMode } from "../ThemeContext";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";

const API_URL =
  "https://withered-sound-8cec.akshar369.workers.dev/?endpoint=hariprasangam";

const CATEGORIES = [
  {
    id: 1,
    en: "P.P. Swamishree Jivandarshan",
    gu: "પ.પૂ. સ્વામીશ્રી જીવનદર્શન",
  },
  { id: 2, en: "Adhyatmik Suz", gu: "અધ્યાત્મિક સૂઝ" },
  { id: 3, en: "Divya Anubhav", gu: "દિવ્ય અનુભવ" },
  { id: 4, en: "Divya Darshan", gu: "દિવ્ય દર્શન" },
  { id: 5, en: "Raksha", gu: "રક્ષા" },
  { id: 6, en: "Ashirvad", gu: "આશીર્વાદ" },
  { id: 7, en: "Swaroopnishtha", gu: "સ્વરૂપનિષ્ઠા" },
  { id: 8, en: "Jivanbhavana", gu: "જીવનભાવના" },
  { id: 9, en: "Abhav-Avagun", gu: "અભાવ-અવગુણ" },
  { id: 10, en: "Dasatva", gu: "દાસત્વ" },
  { id: 11, en: "Bhaktoni Bhakti", gu: "ભક્તોની ભક્તિ" },
  { id: 12, en: "Gurubhakti", gu: "ગુરુભક્તિ" },
  { id: 13, en: "Suhradbhav", gu: "સુહૃદભાવ" },
  { id: 14, en: "Mahima", gu: "મહિમા" },
];

const PRASANG_INDEX_URL = "/data/prasang-index.json";
const LAST_READ_KEY = "hariprasangam-last-read";


function writeCookie(name, value, days = 365) {
  if (typeof document === "undefined") return;
  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const pair = document.cookie
    .split(";")
    .map((s) => s.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function saveLastReadPrasang(prasang) {
  const payload = JSON.stringify({
    PsgId: prasang.PsgId,
    PsgContentFile: prasang.PsgContentFile,
  });
  try {
    localStorage.setItem(LAST_READ_KEY, payload);
  } catch {
    // no-op
  }
  writeCookie(LAST_READ_KEY, payload);
}

function loadLastReadPrasang() {
  let raw = "";
  try {
    raw = localStorage.getItem(LAST_READ_KEY) || "";
  } catch {
    // no-op
  }
  if (!raw) raw = readCookie(LAST_READ_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.PsgId && !parsed?.PsgContentFile) return null;
    return parsed;
  } catch {
    return null;
  }
}

function highlightWords(html, word) {
  if (!word || word.trim().length < 2) return html;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "gi");
  return html.replace(
    re,
    '<mark style="background:#ffe066;color:#222;border-radius:2px">$1</mark>',
  );
}

function getSnippet(content, word) {
  const idx = content.toLowerCase().indexOf(word.toLowerCase());
  if (idx === -1) return "";
  const start = Math.max(0, idx - 80);
  const end = Math.min(content.length, idx + word.length + 80);
  let snippet = content.slice(start, end).replace(/\s+/g, " ").trim();
  if (start > 0) snippet = "..." + snippet;
  if (end < content.length) snippet += "...";
  return snippet;
}

function getSnippetForCandidates(content, candidates) {
  const lower = content.toLowerCase();
  let best = null;

  candidates.forEach((candidate) => {
    if (!candidate) return;
    const idx = lower.indexOf(candidate.toLowerCase());
    if (idx === -1) return;
    if (!best || idx < best.idx) {
      best = { idx, candidate };
    }
  });

  if (!best) return { snippet: "", hit: "" };
  return {
    snippet: getSnippet(content, best.candidate),
    hit: best.candidate,
  };
}

const ROMAN_WORD_OVERRIDES = {
  gunatit: "ગુણાતીત",
  gunatitn: "ગુણાતીત",
};

const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
  "~": "ળ",
  "^": "ટ",
};

const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};

const GUJARATI_PHONETIC_EQUIVALENTS = [
  [/િ|ી/g, "િ"],
  [/ુ|ૂ/g, "ુ"],
  [/ે|ૈ/g, "ે"],
  [/ો|ૌ/g, "ો"],
  [/ઁ|ં|ઃ/g, "ં"],
  [/શ|ષ|સ/g, "સ"],
  [/ળ/g, "લ"],
];

const GUJARATI_INDEPENDENT_VOWELS = {
  અ: "a",
  આ: "aa",
  ઇ: "i",
  ઈ: "i",
  ઉ: "u",
  ઊ: "u",
  ઋ: "ri",
  એ: "e",
  ઐ: "ai",
  ઓ: "o",
  ઔ: "au",
};

const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

const GUJARATI_CONSONANTS = {
  ક: "k",
  ખ: "kh",
  ગ: "g",
  ઘ: "gh",
  ઙ: "ng",
  ચ: "ch",
  છ: "chh",
  જ: "j",
  ઝ: "jh",
  ઞ: "ny",
  ટ: "t",
  ઠ: "th",
  ડ: "d",
  ઢ: "dh",
  ણ: "n",
  ત: "t",
  થ: "th",
  દ: "d",
  ધ: "dh",
  ન: "n",
  પ: "p",
  ફ: "ph",
  બ: "b",
  ભ: "bh",
  મ: "m",
  ય: "y",
  ર: "r",
  લ: "l",
  વ: "v",
  શ: "sh",
  ષ: "sh",
  સ: "s",
  હ: "h",
  ળ: "l",
};

const GUJARATI_SIGNS = {
  "ં": "m",
  "ઃ": "h",
  "ઁ": "m",
};

const GUJARATI_VIRAMA = "્";

function isGujaratiText(text) {
  return /[\u0A80-\u0AFF]/.test(text || "");
}

function normalizeGujaratiPhonetic(text) {
  let normalized = (text || "").toLowerCase().replace(/\s+/g, "");
  GUJARATI_PHONETIC_EQUIVALENTS.forEach(([pattern, replacement]) => {
    normalized = normalized.replace(pattern, replacement);
  });
  return normalized;
}

function normalizeRomanPhonetic(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function gujaratiToRomanPhonetic(text) {
  const input = text || "";
  let out = "";

  for (let i = 0; i < input.length; i += 1) {
    const c = input[i];
    const next = input[i + 1] || "";

    if (c === "ક" && next === "્" && input[i + 2] === "ષ") {
      out += "ksh";
      i += 2;
      continue;
    }
    if (c === "જ" && next === "્" && input[i + 2] === "ઞ") {
      out += "gn";
      i += 2;
      continue;
    }

    if (GUJARATI_INDEPENDENT_VOWELS[c]) {
      out += GUJARATI_INDEPENDENT_VOWELS[c];
      continue;
    }

    if (GUJARATI_CONSONANTS[c]) {
      const base = GUJARATI_CONSONANTS[c];
      const nextChar = input[i + 1] || "";
      if (GUJARATI_MATRAS[nextChar]) {
        out += base + GUJARATI_MATRAS[nextChar];
        i += 1;
        continue;
      }
      if (nextChar === GUJARATI_VIRAMA) {
        out += base;
        i += 1;
        continue;
      }
      out += base + "a";
      continue;
    }

    if (GUJARATI_SIGNS[c]) {
      out += GUJARATI_SIGNS[c];
      continue;
    }

    out += " ";
  }

  out = out.replace(/a(\s|$)/g, "$1");
  return normalizeRomanPhonetic(out);
}

function transliterateRomanWordToGujarati(word) {
  const override = ROMAN_WORD_OVERRIDES[(word || "").toLowerCase()];
  if (override) return override;

  const input = (word || "")
    .replace(/L/g, "~")
    .replace(/T/g, "^")
    .toLowerCase()
    .replace(/[^a-z~^]/g, "");
  if (!input) return "";

  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );

  let out = "";
  let i = 0;
  while (i < input.length) {
    const remaining = input.slice(i);

    let vowelToken = "";
    for (const v of vowels) {
      if (remaining.startsWith(v)) {
        vowelToken = v;
        break;
      }
    }
    if (vowelToken) {
      out += ROMAN_VOWELS[vowelToken].independent;
      i += vowelToken.length;
      continue;
    }

    let consonantToken = "";
    for (const c of consonants) {
      if (remaining.startsWith(c)) {
        consonantToken = c;
        break;
      }
    }
    if (!consonantToken) {
      i += 1;
      continue;
    }

    out += ROMAN_CONSONANTS[consonantToken];
    i += consonantToken.length;

    const afterConsonant = input.slice(i);
    let followingVowel = "";
    for (const v of vowels) {
      if (afterConsonant.startsWith(v)) {
        followingVowel = v;
        break;
      }
    }
    if (followingVowel) {
      out += ROMAN_VOWELS[followingVowel].matra;
      i += followingVowel.length;
    }
  }

  return out;
}

function generateGujaratiLVariants(text) {
  const source = text || "";
  if (!/[લળ]/.test(source)) return [source];

  let variants = [""];
  for (const ch of source) {
    if (ch === "લ" || ch === "ળ") {
      const next = [];
      variants.forEach((prefix) => {
        next.push(prefix + "લ");
        next.push(prefix + "ળ");
      });
      variants = next;
      continue;
    }
    variants = variants.map((prefix) => prefix + ch);
  }
  return Array.from(new Set(variants));
}

function buildWordCandidates(query) {
  const q = (query || "").trim();
  if (!q) return [];

  const candidates = new Set([q]);
  const lowered = q.toLowerCase();

  if (!isGujaratiText(q) && /^[a-z\s]+$/i.test(q)) {
    const transliterated = q
      .split(/\s+/)
      .map((w) => transliterateRomanWordToGujarati(w))
      .filter(Boolean)
      .join(" ")
      .trim();
    if (transliterated) {
      generateGujaratiLVariants(transliterated).forEach((variant) => {
        candidates.add(variant);
        candidates.add(normalizeGujaratiPhonetic(variant));
      });
    }
    if (ROMAN_WORD_OVERRIDES[lowered]) {
      candidates.add(ROMAN_WORD_OVERRIDES[lowered]);
      candidates.add(normalizeGujaratiPhonetic(ROMAN_WORD_OVERRIDES[lowered]));
    }
  }

  if (isGujaratiText(q)) {
    candidates.add(normalizeGujaratiPhonetic(q));
  }

  return Array.from(candidates).filter(Boolean);
}

function HariPrasangam() {
  const [prasangs, setPrasangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [serialSearch, setSerialSearch] = useState("");
  const [contentSearch, setContentSearch] = useState("");
  const [contentIndex, setContentIndex] = useState([]);
  const [contentSearchResults, setContentSearchResults] = useState(null);
  const [selectedCat, setSelectedCat] = useState(null);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [contentLoading, setContentLoading] = useState(false);
  const [fontSize, setFontSize] = useState(1);
  const [darkMode, setDarkMode] = useDarkMode();
  const [resultHighlightWord, setResultHighlightWord] = useState("");
  const [lastReadPrasang, setLastReadPrasang] = useState(() =>
    loadLastReadPrasang(),
  );
  const activeRef = useRef(null);

  useEffect(() => {
    fetch(PRASANG_INDEX_URL)
      .then((r) => r.json())
      .then((data) => setContentIndex(data))
      .catch(() => {});
  }, []);

  const handleContentSearch = useCallback(() => {
    const word = contentSearch.trim();
    if (word.length < 2) return;

    const qLower = word.toLowerCase();
    const queryRomanNormalized = normalizeRomanPhonetic(word);
    const queryIsRoman = !isGujaratiText(word) && /^[a-z\s]+$/i.test(word);
    const candidates = buildWordCandidates(word);
    const normalizedCandidates = candidates.map((c) =>
      normalizeGujaratiPhonetic(c),
    );

    const matchFiles = new Set();
    let firstHitWord = "";

    contentIndex.forEach((entry) => {
      const content = entry.text || "";
      const contentLower = content.toLowerCase();
      const contentPhonetic = normalizeGujaratiPhonetic(content);
      if (!entry._romanPhonetic) {
        entry._romanPhonetic = gujaratiToRomanPhonetic(content);
      }

      let matched = false;
      for (let i = 0; i < candidates.length; i += 1) {
        const candidate = candidates[i];
        const normalized = normalizedCandidates[i];
        if (
          (candidate && contentLower.includes(candidate.toLowerCase())) ||
          (normalized && contentPhonetic.includes(normalized))
        ) {
          matched = true;
          if (!firstHitWord) firstHitWord = candidate;
          break;
        }
      }

      if (
        !matched &&
        queryIsRoman &&
        queryRomanNormalized &&
        entry._romanPhonetic.includes(queryRomanNormalized)
      ) {
        matched = true;
      }

      if (!matched && !contentLower.includes(qLower)) return;

      if (!firstHitWord) {
        const { hit } = getSnippetForCandidates(content, [...candidates, word]);
        firstHitWord = hit || word;
      }

      matchFiles.add(entry.file);
    });

    setContentSearchResults(matchFiles);
    setResultHighlightWord(firstHitWord || word);
  }, [contentIndex, contentSearch]);

  const clearContentSearch = () => {
    setContentSearch("");
    setContentSearchResults(null);
    setResultHighlightWord("");
  };

  useEffect(() => {
    const fetchPrasangs = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL);
        if (!response.ok) {
          throw new Error("Failed to fetch prasangs");
        }
        const data = await response.json();
        setPrasangs(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching prasangs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrasangs();
  }, []);

  const filtered = useMemo(
    () =>
      prasangs.filter(
        (p) =>
          p.PsgId < 430 &&
          (selectedCat === null || p.CatId === selectedCat) &&
          (contentSearchResults === null ||
            contentSearchResults.has(p.PsgContentFile)) &&
          ((p.PsgTitle || "").toLowerCase().includes(search.toLowerCase()) ||
            (p.PsgContent || "").includes(search)) &&
          (!serialSearch.trim() ||
            String(p.PsgId).includes(serialSearch.trim())),
      ),
    [prasangs, selectedCat, contentSearchResults, search, serialSearch],
  );

  const selectedIndex = useMemo(() => {
    if (!selected) return -1;
    return filtered.findIndex((p) => p.PsgId === selected.PsgId);
  }, [filtered, selected]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const openPrasang = useCallback(
    async (prasang) => {
      setSelected(prasang);
      setLastReadPrasang({
        PsgId: prasang.PsgId,
        PsgContentFile: prasang.PsgContentFile,
      });
      saveLastReadPrasang(prasang);

      setContent("");
      setContentLoading(true);
      setFontSize(1);
      try {
        const res = await fetch(
          `${process.env.PUBLIC_URL}/data/prasang/${prasang.PsgContentFile}`,
        );
        if (!res.ok) throw new Error("File not found");
        let html = await res.text();
        html = html.replace(
          /<ul[^>]*class="reader_tools[^"]*"[^>]*>[\s\S]*?<\/ul>/i,
          "",
        );
        const highlightWord =
          contentSearchResults !== null
            ? (resultHighlightWord || contentSearch.trim())
            : "";
        html = highlightWords(html, highlightWord);
        setContent(
          html +
            `<style>
          .vaat_ind { font-family: inherit; line-height: 2; color: ${darkMode ? "#fff" : "#1a1a2e"}; padding: 4px 0; }
          .vaat_ind p { margin: 0 0 14px 0; font-size: 1.05em; line-height: 2; text-align: justify; color: ${darkMode ? "#fff" : "#1a1a2e"}; }
          .title_gu { font-size: 1.2em; font-weight: 700; color: #764ba2; margin-bottom: 16px; line-height: 1.6; text-align: center; }
        </style>`,
        );
      } catch {
        setContent('<p style="color:red">Could not load prasang content.</p>');
      } finally {
        setContentLoading(false);
      }
    },
    [contentSearch, contentSearchResults, darkMode, resultHighlightWord],
  );

  const goToRelativePrasang = useCallback(
    (delta) => {
      if (!selected || selectedIndex < 0) return;
      const targetIndex = selectedIndex + delta;
      if (targetIndex < 0 || targetIndex >= filtered.length) return;
      const target = filtered[targetIndex];
      if (target) openPrasang(target);
    },
    [filtered, openPrasang, selected, selectedIndex],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = (event.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (!selected) return;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativePrasang(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativePrasang(1);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToRelativePrasang, selected]);

  const openLastRead = useCallback(() => {
    if (!lastReadPrasang) return;
    const prasang = prasangs.find(
      (p) =>
        (lastReadPrasang.PsgId && p.PsgId === lastReadPrasang.PsgId) ||
        (lastReadPrasang.PsgContentFile &&
          p.PsgContentFile === lastReadPrasang.PsgContentFile),
    );
    if (prasang) openPrasang(prasang);
  }, [lastReadPrasang, openPrasang, prasangs]);

  return (
    <main
      className={`page-content pravachan-page swamini-vato-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">
          Hari Prasangam
          {!loading && !error && (
            <span
              style={{ fontSize: "1rem", marginLeft: "12px", color: "#888" }}
            >
              ({filtered.length})
            </span>
          )}
        </h1>
        <p className="header-subtitle">
          Guruhari P.P. Hariprasad Swamishri Prasang
        </p>
        {lastReadPrasang && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastRead}
            title="Open last read prasang"
          >
            Last Read: #{lastReadPrasang.PsgId}
          </button>
        )}
      </section>

      <section className="page-body">
        {!loading && !error && (
          <form
            className="word-search-bar"
            onSubmit={(e) => {
              e.preventDefault();
              handleContentSearch();
            }}
          >
            <input
              className="word-search-input"
              type="text"
              placeholder="પ્રસંગમાં શોધ... (Search in content)"
              value={contentSearch}
              onChange={(e) => setContentSearch(e.target.value)}
            />
            <button type="submit" className="word-search-btn">
              Search
            </button>
            <button
              type="button"
              className="word-search-clear"
              onClick={() => setDarkMode((d) => !d)}
              title="Toggle light/dark mode"
            >
              {darkMode ? "Light" : "Dark"}
            </button>
            {contentSearchResults !== null && (
              <button
                type="button"
                className="word-search-clear"
                onClick={clearContentSearch}
              >
                x Clear
              </button>
            )}
          </form>
        )}

        {!loading && !error && contentSearchResults !== null && (
          <div
            style={{
              fontSize: "0.82em",
              color: "#764ba2",
              marginBottom: 14,
              textAlign: "center",
            }}
          >
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} for "
            {contentSearch}"
          </div>
        )}

        {!loading && !error && prasangs.length > 0 && (
          <div
            className="prasang-filter-list"
            role="group"
            aria-label="Categories"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <button
              onClick={() => setSelectedCat(null)}
              aria-pressed={selectedCat === null}
              style={{
                padding: "5px 14px",
                borderRadius: 20,
                fontWeight: 600,
                fontSize: "0.85em",
                cursor: "pointer",
              }}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() =>
                  setSelectedCat(selectedCat === cat.id ? null : cat.id)
                }
                title={cat.en}
                aria-pressed={selectedCat === cat.id}
                style={{
                  padding: "5px 14px",
                  borderRadius: 20,
                  fontWeight: 600,
                  fontSize: "0.85em",
                  cursor: "pointer",
                }}
              >
                {cat.gu}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading prasangs...</p>
          </div>
        )}

        {error && (
          <div className="error-container">
            <p>Error loading prasangs: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <div className="vachanamrut-body">
            <aside className="vachanamrut-list-panel">
              <input
                className="vachanamrut-search"
                type="text"
                placeholder="Filter by title or serial no..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <input
                className="vachanamrut-search vachanamrut-search-serial"
                type="text"
                placeholder="Serial no search (e.g. 6 or 108)"
                value={serialSearch}
                onChange={(e) =>
                  setSerialSearch(e.target.value.replace(/[^\d]/g, ""))
                }
              />
              <ul
                className={`vachanamrut-list${
                  contentSearchResults !== null ? " search-results-scroll-list" : ""
                }`}
              >
                {filtered.map((prasang) => {
                  const isActive = selected?.PsgId === prasang.PsgId;
                  return (
                    <li
                      key={prasang.PsgId}
                      ref={isActive ? activeRef : null}
                      className={`vachanamrut-item${isActive ? " active" : ""}`}
                      aria-current={isActive ? "true" : undefined}
                      onClick={() => openPrasang(prasang)}
                    >
                      <span className="vachan-name">#{prasang.PsgId}</span>
                      <span className="vachan-name-guj">
                        {prasang.PsgTitle}
                      </span>
                      {prasang.PsgContent && (
                        <span
                          className="vachan-name-guj reader-muted"
                          style={{ fontSize: "0.82em", color: "#777" }}
                        >
                          {prasang.PsgContent}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
              {filtered.length === 0 && (
                <p className="reader-muted" style={{ textAlign: "center", color: "#888", padding: 24 }}>
                  No prasangs found.
                </p>
              )}
            </aside>

            <section className="vachanamrut-content-panel">
              {!selected ? (
                <div className="vachanamrut-placeholder">
                  <p>વાંચવા માટે ડાબી બાજુથી પ્રસંગ પસંદ કરો.</p>
                  <p>Select a prasang from the list to read.</p>
                </div>
              ) : (
                <>
                  <div className="font-resize-bar" style={{ marginBottom: 16 }}>
                    <button
                      onClick={() => goToRelativePrasang(-1)}
                      disabled={selectedIndex <= 0}
                      title="Previous (Left Arrow)"
                    >
                      ← Prev
                    </button>
                    <button
                      onClick={() => goToRelativePrasang(1)}
                      disabled={
                        selectedIndex === -1 ||
                        selectedIndex >= filtered.length - 1
                      }
                      title="Next (Right Arrow)"
                    >
                      Next →
                    </button>
                    <button
                      onClick={() =>
                        setFontSize((s) => Math.max(s - 0.15, 0.7))
                      }
                    >
                      A-
                    </button>
                    <button
                      onClick={() =>
                        setFontSize((s) => Math.min(s + 0.15, 2.5))
                      }
                    >
                      A+
                    </button>
                    <span>{Math.round(fontSize * 100)}%</span>
                  </div>
                  {contentLoading ? (
                    <div className="vachanamrut-loading">Loading...</div>
                  ) : (
                    <div
                      className="reader-html"
                      dangerouslySetInnerHTML={{ __html: content }}
                      style={{
                        color: darkMode ? "#fff" : "#1a1a2e",
                        fontFamily: "inherit",
                        lineHeight: 1.8,
                        wordBreak: "break-word",
                        zoom: fontSize,
                      }}
                    />
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </section>
    </main>
  );
}

export default HariPrasangam;
